import React, { useState } from "react";
import { useGetMovesQuery } from "@/store/moves/apis";
import { format } from "date-fns";
import { Move } from "@/types";
import {
  cn,
  formatService,
  formatStartWindow,
  getTimelinePercentages,
  parseTimeString,
} from "@/lib/utils";
import { ArrowRightIcon, BoxIcon, ClockIcon, MapPinIcon } from "lucide-react";
import { faPeopleCarryBox, faTruck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@/components/ui/button";
import { AssignCrew } from "@/pages/Jobs/AssignCrew";
import { Loading } from "@/components/Loading";
import { useGetCalendarEventsQuery } from "@/store/mover/apis";
import { CalendarEvent } from "@/types";
import { MOVE_COLORS } from "@/constants";
import { Link } from "react-router-dom";

interface CalendarProps {
  onDate: Date;
  dispatchMode?: boolean;
}

const DayCalendar: React.FC<CalendarProps> = ({
  onDate,
  dispatchMode = false,
}) => {
  const {
    data: moves,
    isLoading,
    error,
    refetch,
  } = useGetMovesQuery({
    type: "jobs",
    after: format(onDate, "yyyy-MM-dd"),
    before: format(onDate, "yyyy-MM-dd"),
    include: ["locations", "crew"],
  });

  const { data: onsiteEvents } = useGetCalendarEventsQuery({
    nature: "onsite",
  });

  const { data: blockEvents } = useGetCalendarEventsQuery({
    nature: "block",
  });

  const [selectedMoveForCrew, setSelectedMoveForCrew] = useState<
    Move | undefined
  >(undefined);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  if (isLoading)
    return (
      <div className="mt-4 h-[calc(100vh-100px)]">
        <Loading />
      </div>
    );

  if (error) return <div>Error loading moves</div>;

  const dateString = format(onDate, "yyyy-MM-dd");
  const blocks =
    blockEvents?.filter((event: CalendarEvent) => event.date === dateString) ||
    [];
  const onsites =
    onsiteEvents?.filter((event: CalendarEvent) => event.date === dateString) ||
    [];
  const isBlocked = blocks.length > 0;

  if (isBlocked) {
    return (
      <div className="h-[calc(100vh-100px)]">
        <div className="flex flex-col items-center justify-center h-full">
          <div className="text-xl font-medium">
            No events or moves scheduled on this day
          </div>
          <div className="text-slate-700 font-medium bg-slate-100 rounded-md px-3 py-1.5 shadow-sm flex items-center gap-2 mt-2">
            <span>🚫</span>
            <span>Blocked</span>
          </div>
        </div>
      </div>
    );
  }

  if (moves.length === 0 && onsites.length === 0) {
    return (
      <div className="h-[calc(100vh-100px)]">
        <div className="flex flex-col items-center justify-center h-full">
          <div className="text-xl font-medium">
            No events or moves scheduled on this day
          </div>
        </div>
      </div>
    );
  }

  const hours = Array.from({ length: 13 }, (_, i) => i + 6); // 6AM to 6PM

  return (
    <div className="h-full overflow-y-auto">
      <div className="pt-12 relative w-full h-full">
        {moves.map((move: Move) => {
          const hour =
            move.start_window_begin_hour! +
            move.start_window_begin_minute! / 60;
          let startHour;
          if (move.start_window_begin_meridiem === "AM") {
            startHour = hour;
          } else if (move.start_window_begin_hour === 12) {
            startHour = hour;
          } else {
            startHour = hour + 12;
          }
          const startPercentage = ((startHour! - 6) / 12) * 100;
          const durationPercentage = ((move.hours_estimate! + 1) / 12) * 100;

          return (
            <div
              key={move.id}
              className="relative mb-4 z-[5] bg-stone-50 border-l border-b border-r border-stone-150 shadow-sm"
              style={{
                left: `${startPercentage}%`,
                minWidth: `${durationPercentage}%`,
                width: "fit-content",
              }}
            >
              <div className={cn("text-sm text-black")}>
                <h3
                  className={cn(
                    "mb-2 text-sm font-bold text-white px-2 py-0.5",
                    `${
                      MOVE_COLORS[move.service as keyof typeof MOVE_COLORS] ||
                      MOVE_COLORS.default
                    }`
                  )}
                >
                  <Link className="text-white" to={`/move/${move.id}`}>
                    {move.customer_name}
                  </Link>
                  <span className="text-sm font-normal"> (#{move.number})</span>
                </h3>
                <div className="flex flex-row items-start gap-4 px-2 pb-2">
                  <div className="flex flex-col gap-0.5 text-xs">
                    <div className="flex items-center">
                      <ClockIcon className="w-3 h-3 mr-1" />
                      Arrive{" "}
                      {formatStartWindow(
                        move.start_window_begin_hour,
                        move.start_window_begin_minute,
                        move.start_window_begin_meridiem
                      )}{" "}
                      to{" "}
                      {formatStartWindow(
                        move.start_window_end_hour,
                        move.start_window_end_minute,
                        move.start_window_end_meridiem
                      )}{" "}
                      ({parseInt(move.hours_estimate?.toString() || "2")}-
                      {parseInt(move.hours_estimate?.toString() || "2") + 1}{" "}
                      hrs)
                    </div>
                    <div className="flex items-center">
                      <BoxIcon className="w-3 h-3 mr-1" />
                      {formatService(move)}
                    </div>
                    <div className="flex flex-row items-center gap-0.5">
                      <MapPinIcon className="w-3 h-3 mr-1" />
                      <div className="flex-1 flex flex-row items-center gap-0.5">
                        <div>{move.origin?.city}</div>
                        {move.stops.map((stop) => (
                          <div
                            className="flex flex-row items-center gap-0.5"
                            key={stop.id}
                          >
                            <ArrowRightIcon className="w-3 h-3" /> {stop.city}
                          </div>
                        ))}
                        {move.destination?.city && (
                          <div className="flex flex-row items-center gap-0.5">
                            <ArrowRightIcon className="w-3 h-3" />{" "}
                            {move.destination?.city}
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      className={cn(
                        "mt-2 items-center hidden",
                        dispatchMode && "flex"
                      )}
                    >
                      <Button
                        variant="link"
                        className="text-black p-0 underline text-base lg:text-sm h-4 font-semibold"
                        onClick={() => {
                          setSelectedMoveForCrew(move);
                          setIsSheetOpen(true);
                        }}
                      >
                        Dispatch Crew
                      </Button>
                    </div>
                  </div>
                  <div
                    className={cn(
                      "text-xs hidden",
                      dispatchMode && "block border-l border-stone-150 pl-4"
                    )}
                  >
                    <div className="flex items-start gap-1">
                      <div className="flex flex-row items-center gap-0.5">
                        <FontAwesomeIcon
                          icon={faPeopleCarryBox}
                          className="w-3 h-3"
                        />
                        {move.moving_crews?.length} of {move.num_of_movers}{" "}
                        movers assigned
                      </div>
                    </div>

                    <div className="mt-1.5 flex flex-col gap-0.5">
                      {[...(move.moving_crews || [])]
                        .sort((a, b) => b.role.localeCompare(a.role))
                        .map((crew) => (
                          <div
                            key={crew.id}
                            className={cn(
                              "flex flex-row items-center gap-0.5",
                              crew.role === "leader" && "font-semibold"
                            )}
                          >
                            {crew.first_name} (
                            {crew.role[0].toUpperCase() + crew.role.slice(1)})
                          </div>
                        ))}
                    </div>

                    <div className="mt-2 flex items-center gap-0.5">
                      <FontAwesomeIcon icon={faTruck} className="w-3 h-3" />
                      {move.trucks?.length || 0} of {move.num_of_trucks} trucks
                      assigned
                    </div>

                    <div className="mt-1.5 flex flex-col gap-0.5">
                      {move.trucks?.map((truck) => (
                        <div
                          key={truck.id}
                          className="flex flex-row items-center gap-0.5"
                        >
                          {truck.name}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {onsites.map((event: CalendarEvent) => {
          const startHour = parseTimeString(event.start_time);
          const endHour = parseTimeString(event.end_time);
          const { startPercentage, durationPercentage } =
            getTimelinePercentages(startHour, endHour);

          return (
            <div
              key={event.id}
              className="absolute mb-4 z-[5] bg-emerald-50 border-l border-b border-r border-emerald-150 shadow-sm"
              style={{
                left: `${startPercentage}%`,
                minWidth: `${durationPercentage}%`,
                width: "fit-content",
              }}
            >
              <div className="text-sm text-black">
                <h3
                  className={cn(
                    "mb-2 text-sm font-bold text-white px-2 py-0.5",
                    MOVE_COLORS.onsite
                  )}
                >
                  <Link className="text-white" to={`/move/${event.move_id}`}>
                    {event.title}
                  </Link>
                </h3>
                <div className="flex flex-row items-start gap-4 px-2 pb-2">
                  <div className="flex flex-col gap-0.5 text-xs">
                    <div className="flex items-center">
                      <ClockIcon className="w-3 h-3 mr-1" />
                      {event.start_time} - {event.end_time}
                    </div>
                    <div className="flex items-center">
                      <MapPinIcon className="w-3 h-3 mr-1" />
                      {event.address}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none">
          {hours.slice(0, 12).map((hour) => (
            <div
              key={hour}
              className="absolute h-full font-medium text-sm border-b border-l border-stone-100 border-dashed"
              style={{ left: `${((hour - 6) / 12) * 100}%` }}
            >
              <div className="pl-1">
                {format(new Date().setHours(hour, 0, 0, 0), "ha")}
              </div>
            </div>
          ))}
        </div>
      </div>

      <AssignCrew
        move_id={selectedMoveForCrew?.id}
        open={isSheetOpen}
        onOpenChange={(open) => {
          setIsSheetOpen(open);
          if (!open) {
            setSelectedMoveForCrew(undefined);
            refetch();
          }
        }}
      />
    </div>
  );
};

export default DayCalendar;
